"use client"

import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import { formatAmount } from "@/utils/constants";


import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import styles from "./UserSettingsModal.module.css"

export default function UserSettingsModal({ isOpen, onClose, userData, onSave }) {
  const { user, role } = useSelector((state) => state.auth);

  // Calculate Max Limit available to assign (Parent Available + Current User Limit)
  // user.credit is assumed to be the logged-in user's available credit
  const myAvailableCredit = user?.credit ? parseFloat(user.credit) : 0;
  // If owner, infinite. If not, MyAvailable + TargetCurrentLimit
  const maxCreditLimit = userData?.creditLimit ? myAvailableCredit + parseFloat(userData.creditLimit) : myAvailableCredit;

  const isOwner = role === 'owner';

  const [formData, setFormData] = useState({
    username: "",
    userRate: "1.000",
    password: "",
    repeatPassword: "",
    userStatus: "ACTIVE",
    betStatus: "ACTIVE",
    changeExposure: "0.00",
    newExposureLimit: "",
    notes: "",
    masterPassword: "",
    creditLimit: "0.00",
    newCreditLimit: "",
    availableCredit: "0.00",
    lowCreditLimit: "0.00",

  })

  // Populate form data when userData changes or modal opens
  useEffect(() => {
    if (isOpen && userData) {
      // Min value = CreditLimit - AvailableCredit - NetExposure
      // Note: NetExposure is commonly negative if losing, positive if winning? 
      // Usually "Exposure" strictly means liability, so it's a positive number representing risk.
      // Formula: selecteduser creditlimit - available credit + netexposure
      const currentLimit = parseFloat(userData.creditLimit || 0);
      const available = parseFloat(userData.availableCredit || 0);
      const exposure = parseFloat(userData.netExposure || 0);

      let minLimit = currentLimit - available + exposure;
      if (minLimit < 0) minLimit = 0; // Safety check

      setFormData({
        username: userData.loginName || "",
        userRate: "1.000", // Default or from userData if available
        password: "",
        repeatPassword: "",
        userStatus: userData.status === "Active" ? "ACTIVE" : "INACTIVE",
        betStatus: userData.bet_locked ? "INACTIVE" : "ACTIVE",
        changeExposure: userData.expLimit !== null && userData.expLimit !== undefined ? formatAmount(userData.expLimit) : "∞", // Display Limit, infinity if null? or 0.
        newExposureLimit: "",
        notes: "",
        masterPassword: "",
        creditLimit: userData.creditLimit ? formatAmount(userData.creditLimit) : "0",
        availableCredit: userData.availableCredit ? formatAmount(userData.availableCredit) : "0",
        newCreditLimit: "",
        lowCreditLimit: formatAmount(minLimit),
      })
    }
  }, [isOpen, userData])

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleRadioChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleClose = () => {
    if (onClose) onClose();
  }

  const handleCancel = () => {
    if (onClose) onClose();
  }

  const handleSave = async () => {
    if (!formData.masterPassword) {
      alert("Please enter Master Password (Transaction Password)");
      return;
    }

    const {
      userStatus,
      betStatus,
      newCreditLimit,
      newExposureLimit,
      password,
      repeatPassword,
      masterPassword
    } = formData;

    // Common payload parts
    const basePayload = {
      userId: userData.user_id || userData.staff_id || userData.id,
      userType: userData.user_type || userData.role || userData.accountType,
      transactionPassword: masterPassword
    };

    const promises = [];

    // 1. Password Update
    if (password) {
      if (password !== repeatPassword) {
        alert("Passwords do not match!");
        return;
      }
      promises.push(
        api.post(API_ENDPOINTS.LORDS.UPDATE_PASSWORD, {
          ...basePayload,
          password
        }).then(res => ({ type: 'Password', success: res.data.success, msg: res.data.message }))
          .catch(err => ({ type: 'Password', success: false, msg: err.response?.data?.message || err.message }))
      );
    }

    // 2. Status Update (Check if changed from initial or just send if valid)
    // To be strictly "only if changed", we should compare with userData.
    // However, users might just want to be sure. Let's send if currently selected.
    // Comparing with initial userData for better UX (avoid unnecessary calls).
    const initialUserStatus = userData.status === "Active" ? "ACTIVE" : "INACTIVE";
    const initialBetStatus = userData.bet_locked ? "INACTIVE" : "ACTIVE";

    if (userStatus !== initialUserStatus || betStatus !== initialBetStatus) {
      promises.push(
        api.post(API_ENDPOINTS.LORDS.UPDATE_STATUS, {
          ...basePayload,
          userStatus,
          betStatus
        }).then(res => ({ type: 'Status', success: res.data.success, msg: res.data.message }))
          .catch(err => ({ type: 'Status', success: false, msg: err.response?.data?.message || err.message }))
      );
    }

    // 3. Credit Limit Update
    if (newCreditLimit) {
      // Validate Credit Limit
      const creditVal = parseFloat(newCreditLimit);
      if (isNaN(creditVal) || creditVal < 0) {
        alert("Invalid credit limit.");
        return;
      }

      const minVal = parseFloat(formData.lowCreditLimit);

      if (creditVal < minVal) {
        alert(`Credit limit cannot be less than usage (${formatAmount(minVal)})`);
        return;
      }

      if (!isOwner && creditVal > maxCreditLimit) {
        alert(`Credit limit cannot exceed your available limit of ${formatAmount(maxCreditLimit)}`);
        return;
      }

      promises.push(
        api.post(API_ENDPOINTS.LORDS.UPDATE_CREDIT_LIMIT, {
          ...basePayload,
          newCreditLimit
        }).then(res => ({ type: 'Credit Limit', success: res.data.success, msg: res.data.message }))
          .catch(err => ({ type: 'Credit Limit', success: false, msg: err.response?.data?.message || err.message }))
      );
    }

    // 4. Exposure Limit Update
    if (newExposureLimit) {
      promises.push(
        api.post(API_ENDPOINTS.LORDS.UPDATE_EXPOSURE_LIMIT, {
          ...basePayload,
          newExposureLimit
        }).then(res => ({ type: 'Exposure Limit', success: res.data.success, msg: res.data.message }))
          .catch(err => ({ type: 'Exposure Limit', success: false, msg: err.response?.data?.message || err.message }))
      );
    }

    if (promises.length === 0) {
      alert("No changes to save.");
      return;
    }

    try {
      const results = await Promise.all(promises);

      const failures = results.filter(r => !r.success);
      const successes = results.filter(r => r.success);

      if (failures.length > 0) {
        alert(`Failed updates:\n${failures.map(f => `${f.type}: ${f.msg}`).join('\n')}`);
      }

      if (successes.length > 0) {
        alert(`Successfully updated: ${successes.map(s => s.type).join(', ')}`);
        if (onSave) onSave(formData); // Determine if we should only call this if ALL success?
        // Usually safe to refresh data even if partial success.
        handleClose();
      }

    } catch (error) {
      console.error("Error saving settings:", error);
      alert("An unexpected error occurred.");
    }
  }

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalContainer}>
        <div className={styles.closeButton} onClick={handleClose}>
          ×
        </div>


        {/* Information Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Information</h2>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Username</label>
            <div className={styles.displayValue}>{formData.username}</div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>User Rate</label>
            <input
              type="text"
              name="userRate"
              value={formData.userRate}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        {/* Change Password Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Change Password</h2>

          <div className={styles.twoColumnLayout}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Password</label>
              <input
                type="password"
                name="password"
                placeholder="New Password"
                value={formData.password}
                onChange={handleInputChange}
                className={styles.input}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Repeat Password</label>
              <input
                type="password"
                name="repeatPassword"
                placeholder="Re Type Password"
                value={formData.repeatPassword}
                onChange={handleInputChange}
                className={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Change Status Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Change Status</h2>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>User Status</label>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="userStatus"
                  value="ACTIVE"
                  checked={formData.userStatus === "ACTIVE"}
                  onChange={handleRadioChange}
                  className={styles.radio}
                />
                <span className={styles.radioText}>ACTIVE</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="userStatus"
                  value="INACTIVE"
                  checked={formData.userStatus === "INACTIVE"}
                  onChange={handleRadioChange}
                  className={styles.radio}
                />
                <span className={styles.radioText}>INACTIVE</span>
              </label>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Bet Status</label>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="betStatus"
                  value="ACTIVE"
                  checked={formData.betStatus === "ACTIVE"}
                  onChange={handleRadioChange}
                  className={styles.radio}
                />
                <span className={styles.radioText}>ACTIVE</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="betStatus"
                  value="INACTIVE"
                  checked={formData.betStatus === "INACTIVE"}
                  onChange={handleRadioChange}
                  className={styles.radio}
                />
                <span className={styles.radioText}>INACTIVE</span>
              </label>
            </div>
          </div>
        </div>

        {/* Credit Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Credit</h2>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Credit Limit</label>
            <input type="text" value={formData.creditLimit} className={styles.inputDisabled} disabled />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>New Credit Limit</label>
            <div className={styles.validationGroup}>
              <input
                type="text"
                name="newCreditLimit"
                placeholder="Credit Limit"
                value={formData.newCreditLimit}
                onChange={handleInputChange}
                className={styles.input}
              />
              <div className={styles.validationText}>
                {` >= ${formData.lowCreditLimit}`}
                <br />
                {/* Display dynamic limit or indicate no limit for owners */}
                {!isOwner && ` <= ${formatAmount(maxCreditLimit)}`}
                {isOwner && " (No Limit)"}
              </div></div>
          </div>
        </div>

        {/* Change Exposure Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Change Exposure Limit</h2>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Current Limit</label>
            <input type="text" value={formData.changeExposure} className={styles.inputDisabled} disabled />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>New Exposure Limit</label>
            <input
              type="text"
              name="newExposureLimit"
              placeholder="Enter New Limit"
              value={formData.newExposureLimit}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        {/* Notes Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Notes</h2>

          <div className={styles.fieldGroup}>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} className={styles.textarea} />
          </div>
        </div>

        {/* Master Password Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Master Password</h2>

          <div className={styles.fieldGroup}>
            <input
              type="password"
              name="masterPassword"
              placeholder="Master Password"
              value={formData.masterPassword}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className={styles.buttonGroup}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
